import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { Cloud, Database, Settings, Save, TestTube, ArrowLeft, RefreshCw, CheckCircle } from 'lucide-react';
import { integrationService } from '../../services';
import FaceMatchingProvider from '../../components/persons/FaceMatchingProvider';

interface S3Configuration {
  id?: number;
  bucket_name: string;
  region: string;
  access_key_id: string;
  secret_access_key: string;
  folder_structure: 'location' | 'date' | 'location_date';
  is_active: boolean;
  ai_metadata_enabled: boolean;
  event_video_upload_enabled: boolean;
  is_inherited?: boolean;
}

const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [migrationResult, setMigrationResult] = useState<{migrated: number; failed: number; skipped: number} | null>(null);
  const [locationsWithExternalAccess, setLocationsWithExternalAccess] = useState<{id: number; name: string}[]>([]);
  const [checkingLocations, setCheckingLocations] = useState(false);
  const [config, setConfig] = useState<S3Configuration>({
    bucket_name: '',
    region: 'us-east-1',
    access_key_id: '',
    secret_access_key: '',
    folder_structure: 'location',
    is_active: false, // Default to inactive when no config exists
    ai_metadata_enabled: false, // Default to disabled
    event_video_upload_enabled: false,
  });
  const [originalAccessKey, setOriginalAccessKey] = useState<string>('');
  const [credentialsChanged, setCredentialsChanged] = useState(false);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setIsLoading(true);
    try {
      const data = await integrationService.getS3Config();
      if (data) {
        setConfig({
          ...data,
          // Show placeholder for existing credentials
          access_key_id: data.access_key_id?.includes('*') ? data.access_key_id : data.access_key_id,
          secret_access_key: '', // Backend doesn't return this for security
          ai_metadata_enabled: data.ai_metadata_enabled ?? true, // Default to enabled
          event_video_upload_enabled: data.event_video_upload_enabled ?? false // Default to disabled
        });
        setOriginalAccessKey(data.access_key_id || '');
      } else {
        // No config exists - ensure inactive state
        console.log('No existing configuration found - showing inactive form');
        setConfig({
          bucket_name: '',
          region: 'us-east-1',
          access_key_id: '',
          secret_access_key: '',
          folder_structure: 'location',
          is_active: false,
          ai_metadata_enabled: false,
          event_video_upload_enabled: false,
        });
      }
    } catch (err) {
      // If no config exists, that's fine - we'll create one
      console.log('No existing configuration found - showing inactive form');
      setConfig({
        bucket_name: '',
        region: 'us-east-1',
        access_key_id: '',
        secret_access_key: '',
        folder_structure: 'location',
        is_active: false,
        ai_metadata_enabled: false,
        event_video_upload_enabled: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate credentials if changed or new config
      if (!config.id || credentialsChanged) {
        if (!config.access_key_id || config.access_key_id.includes('*')) {
          setError('Please enter AWS Access Key ID');
          setIsSaving(false);
          return;
        }
        if (!config.secret_access_key) {
          setError('Please enter AWS Secret Access Key');
          setIsSaving(false);
          return;
        }
      }

      if (config.id) {
        // For updates: only send credentials if they changed
        const dataToSend: any = {
          bucket_name: config.bucket_name,
          region: config.region,
          folder_structure: config.folder_structure,
          is_active: config.is_active,
          ai_metadata_enabled: config.ai_metadata_enabled,
          event_video_upload_enabled: config.event_video_upload_enabled
        };
        
        // Only include credentials if user changed them
        if (credentialsChanged) {
          dataToSend.access_key_id = config.access_key_id;
          dataToSend.secret_access_key = config.secret_access_key;
        }
        
        const updated = await integrationService.updateS3Config(dataToSend);
        setConfig({
          ...updated,
          secret_access_key: '', // Clear for security
          ai_metadata_enabled: updated.ai_metadata_enabled ?? true, // Default to enabled
          event_video_upload_enabled: updated.event_video_upload_enabled ?? false
        });
        setOriginalAccessKey(updated.access_key_id || '');
        setCredentialsChanged(false);
        setSuccess('S3 configuration updated successfully!');
      } else {
        const newConfig = await integrationService.createS3Config(config);
        setConfig({
          ...newConfig,
          secret_access_key: '', // Clear for security
          ai_metadata_enabled: newConfig.ai_metadata_enabled ?? true, // Default to enabled
          event_video_upload_enabled: newConfig.event_video_upload_enabled ?? false
        });
        setOriginalAccessKey(newConfig.access_key_id || '');
        setCredentialsChanged(false);
        setSuccess('S3 configuration created successfully!');
      }
      
      // Refresh to get latest data
      await fetchConfiguration();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await integrationService.testS3Connection(config);
      if (result.success) {
        setSuccess('Connection test successful! S3 bucket is accessible.');
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test connection');
    } finally {
      setIsTesting(false);
    }
  };

  const handleMigrateEvents = async () => {
    setIsMigrating(true);
    setError(null);
    setSuccess(null);
    setMigrationResult(null);

    try {
      const result = await integrationService.migrateEventsToS3(undefined, 100);
      if (result.success) {
        setMigrationResult({
          migrated: result.migrated,
          failed: result.failed,
          skipped: result.skipped
        });
        setSuccess(`✅ Migration complete! ${result.migrated} events moved to your S3, ${result.skipped} already migrated, ${result.failed} failed`);
      } else {
        setError(result.message || 'Migration failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to migrate events');
    } finally {
      setIsMigrating(false);
    }
  };

  const checkLocationsWithExternalAccess = async () => {
    setCheckingLocations(true);
    try {
      const result = await integrationService.getLocationsWithExternalAccess();
      if (result.success) {
        setLocationsWithExternalAccess(result.locations.map(loc => ({ id: loc.id, name: loc.name })));
        return result.count > 0;
      }
      return false;
    } catch (err) {
      console.error('Error checking locations:', err);
      return false;
    } finally {
      setCheckingLocations(false);
    }
  };

  const handleEventVideoUploadToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    
    if (checked) {
      // Check if any locations have external access before enabling
      setCheckingLocations(true);
      const hasExternalAccess = await checkLocationsWithExternalAccess();
      
      if (!hasExternalAccess) {
        setError('No locations with external access (DDNS) configured. Please configure at least one location with external access before enabling event video uploads.');
        // Keep checkbox unchecked
        setConfig({ ...config, event_video_upload_enabled: false });
        return;
      }
      setSuccess(`Found ${locationsWithExternalAccess.length} location(s) with external access configured. Event videos will be recorded automatically for these locations.`);
      setConfig({ ...config, event_video_upload_enabled: true });
    } else {
      // Allow disabling without checks
      setConfig({ ...config, event_video_upload_enabled: false });
      setSuccess(null);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-96">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                onClick={() => navigate('/company')}
                className="p-2"
              >
                <ArrowLeft size={20} />
              </Button>
              <h1 className="text-2xl font-bold text-text-primary">Cloud Integrations</h1>
            </div>
            <p className="text-text-muted ml-12">Configure event storage and your company’s face-matching provider</p>
          </div>
          <Badge variant={config.is_active ? 'success' : 'default'}>
            {config.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {success && (
          <Alert variant="success" message={success} onClose={() => setSuccess(null)} />
        )}

        {config.is_inherited && (
          <Alert 
            variant="info" 
            message="You are viewing the parent company's S3 configuration. Changes will affect the parent account." 
          />
        )}

        <FaceMatchingProvider />

        {/* S3 Configuration Card */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-accent-primary/10 rounded-xl flex items-center justify-center">
                <Cloud size={24} className="text-accent-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary">Amazon S3 Configuration</h2>
                <p className="text-sm text-text-muted">Upload event media to your S3 bucket</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Bucket Name */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Bucket Name *
                </label>
                <input
                  type="text"
                  value={config.bucket_name}
                  onChange={(e) => setConfig({ ...config, bucket_name: e.target.value })}
                  className="w-full px-4 py-2 bg-surface-secondary border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary text-text-primary"
                  placeholder="my-secureview-bucket"
                  required
                />
                <p className="mt-1 text-xs text-text-muted">
                  The name of your S3 bucket where events will be uploaded
                </p>
              </div>

              {/* Region */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  AWS Region *
                </label>
                <select
                  value={config.region}
                  onChange={(e) => setConfig({ ...config, region: e.target.value })}
                  className="w-full px-4 py-2 bg-surface-secondary border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary text-text-primary"
                  required
                >
                  <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                  <option value="us-east-2">US East (Ohio) - us-east-2</option>
                  <option value="us-west-1">US West (N. California) - us-west-1</option>
                  <option value="us-west-2">US West (Oregon) - us-west-2</option>
                  <option value="ca-central-1">Canada (Central) - ca-central-1</option>
                  <option value="eu-west-1">Europe (Ireland) - eu-west-1</option>
                  <option value="eu-central-1">Europe (Frankfurt) - eu-central-1</option>
                  <option value="ap-south-1">Asia Pacific (Mumbai) - ap-south-1</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</option>
                </select>
              </div>

              {/* Access Key ID */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Access Key ID *
                  {config.id && originalAccessKey.includes('*') && (
                    <span className="ml-2 text-xs text-green-500">(saved securely)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={config.access_key_id}
                  onChange={(e) => {
                    setConfig({ ...config, access_key_id: e.target.value });
                    setCredentialsChanged(true);
                  }}
                  className="w-full px-4 py-2 bg-surface-secondary border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary text-text-primary font-mono"
                  placeholder={config.id && originalAccessKey.includes('*') ? "Leave unchanged or enter new key" : "AKIAIOSFODNN7EXAMPLE"}
                  required={!config.id}
                />
                {config.id && originalAccessKey.includes('*') && !credentialsChanged && (
                  <p className="mt-1 text-xs text-blue-500">
                    ✓ Credentials are saved and encrypted. Leave blank to keep current credentials.
                  </p>
                )}
              </div>

              {/* Secret Access Key */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Secret Access Key *
                  {config.id && originalAccessKey.includes('*') && (
                    <span className="ml-2 text-xs text-green-500">(saved securely)</span>
                  )}
                </label>
                <input
                  type="password"
                  value={config.secret_access_key}
                  onChange={(e) => {
                    setConfig({ ...config, secret_access_key: e.target.value });
                    setCredentialsChanged(true);
                  }}
                  className="w-full px-4 py-2 bg-surface-secondary border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary text-text-primary font-mono"
                  placeholder={config.id && originalAccessKey.includes('*') ? "Leave unchanged or enter new secret" : "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"}
                  required={!config.id}
                />
                <p className="mt-1 text-xs text-text-muted">
                  {config.id && originalAccessKey.includes('*')
                    ? "✓ Secret key is stored encrypted. Only enter if you want to change it."
                    : "Your AWS secret access key (stored encrypted)"
                  }
                </p>
              </div>

              {/* Folder Structure */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Folder Organization
                </label>
                <select
                  value={config.folder_structure}
                  onChange={(e) => setConfig({ ...config, folder_structure: e.target.value as any })}
                  className="w-full px-4 py-2 bg-surface-secondary border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary text-text-primary"
                >
                  <option value="location">By Location Name</option>
                  <option value="date">By Date (YYYY-MM-DD)</option>
                  <option value="location_date">By Location and Date</option>
                </select>
                <p className="mt-1 text-xs text-text-muted">
                  How events should be organized in your S3 bucket
                </p>
              </div>

              {/* Status Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={config.is_active}
                  onChange={(e) => setConfig({ ...config, is_active: e.target.checked })}
                  className="w-4 h-4 text-accent-primary bg-surface-secondary border-border-primary rounded focus:ring-2 focus:ring-accent-primary"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-text-primary">
                  Enable automatic S3 uploads for new events
                </label>
              </div>

              {/* AI Metadata Processing Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="ai_metadata_enabled"
                  checked={config.ai_metadata_enabled}
                  onChange={(e) => setConfig({ ...config, ai_metadata_enabled: e.target.checked })}
                  className="w-4 h-4 text-accent-primary bg-surface-secondary border-border-primary rounded focus:ring-2 focus:ring-accent-primary"
                />
                <label htmlFor="ai_metadata_enabled" className="text-sm font-medium text-text-primary">
                  Enable AI Metadata Processing
                </label>
              </div>
              <p className="mt-1 text-xs text-text-muted -mt-3 ml-7">
                Automatically analyze uploaded images using AWS Bedrock Nova for enhanced event metadata
              </p>

              {/* Event Video Upload Toggle */}
              <div className="border-t border-border-primary pt-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="event_video_upload_enabled"
                    checked={config.event_video_upload_enabled}
                    onChange={handleEventVideoUploadToggle}
                    disabled={checkingLocations}
                    className="w-4 h-4 text-accent-primary bg-surface-secondary border-border-primary rounded focus:ring-2 focus:ring-accent-primary disabled:opacity-50"
                  />
                  <label htmlFor="event_video_upload_enabled" className="text-sm font-medium text-text-primary">
                    Enable Event Video Recording & Upload
                    {checkingLocations && <Loading size="sm" className="inline ml-2" />}
                  </label>
                </div>
                <p className="mt-1 text-xs text-text-muted ml-7">
                  Automatically record video clips (±30 seconds) for each event via DDNS and upload to S3. 
                  Requires locations to have external access (DDNS) configured.
                </p>
                {config.event_video_upload_enabled && locationsWithExternalAccess.length > 0 && (
                  <div className="mt-2 ml-7 p-2 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-600">
                    ✓ Enabled for {locationsWithExternalAccess.length} location(s): {locationsWithExternalAccess.map(l => l.name).join(', ')}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1"
                >
                  {isSaving ? (
                    <>
                      <Loading size="sm" className="mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="mr-2" />
                      Save Configuration
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={isTesting || !config.bucket_name || !config.access_key_id || !config.secret_access_key}
                  className="flex-1"
                >
                  {isTesting ? (
                    <>
                      <Loading size="sm" className="mr-2" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube size={18} className="mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-500/5 border-blue-500/20">
          <div className="p-6">
            <div className="flex gap-3">
              <Database size={20} className="text-blue-500 shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary space-y-2">
                <p className="font-medium text-text-primary">How it works:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Events will be automatically uploaded to your S3 bucket</li>
                  <li>Each event will have its own copy (not a reference link)</li>
                  <li>Events are organized in folders based on location name</li>
                  <li>S3 links will be stored with each event for easy access</li>
                  <li>Your AWS credentials are encrypted and stored securely</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Migration Card */}
        {config.id && (
          <Card>
            <div className="p-6 border-b border-surface-secondary">
              <h2 className="text-xl font-semibold text-text-primary flex items-center gap-2">
                <RefreshCw size={24} className="text-accent-primary" />
                Migrate Existing Events
              </h2>
              <p className="text-sm text-text-muted mt-1">
                Move event images from HikVision, EZVIZ, and other cloud providers to your S3 bucket
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-sm text-text-secondary">
                  This will download images from external cloud providers (HikVision, EZVIZ) and upload them to your S3 bucket.
                  This gives you full control over your event media and prevents broken links when external URLs expire.
                </p>
              </div>

              {migrationResult && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-500">{migrationResult.migrated}</p>
                    <p className="text-xs text-text-muted mt-1">Migrated</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-500">{migrationResult.skipped}</p>
                    <p className="text-xs text-text-muted mt-1">Skipped</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-500">{migrationResult.failed}</p>
                    <p className="text-xs text-text-muted mt-1">Failed</p>
                  </div>
                </div>
              )}

              <Button
                type="button"
                variant="primary"
                onClick={handleMigrateEvents}
                disabled={isMigrating}
                className="w-full"
              >
                {isMigrating ? (
                  <>
                    <Loading size="sm" className="mr-2" />
                    Migrating Events...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} className="mr-2" />
                    Migrate 100 Events to S3
                  </>
                )}
              </Button>

              <p className="text-xs text-text-muted text-center">
                Processes 100 events per click. Run multiple times to migrate all events.
              </p>
            </div>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default IntegrationsPage;
